import type { ReactElement } from 'react'

import { Table, Tag } from '../../components'
import type { TableColumn, TagTone } from '../../components'
import { sectionLabel } from '../../lib/sections'
import type { RouterOutputs } from '../../trpc'
import { adminStyles as styles, formatDateTime } from './AdminPage'

export type ConfigChange = RouterOutputs['configuration']['listChanges'][number]
export type ConfigChangeEntity = ConfigChange['entity']
type ConfigChangeAction = ConfigChange['action']
type ChangeState = ConfigChange['before']

export const ENTITY_LABEL: Record<ConfigChangeEntity, string> = {
  machine_group: 'Máquina → grupo',
  plan_machine: 'Máquina del plan',
  quality_rule: 'Regla de calidad',
  special_schedule: 'Cocción baja',
  bm_homologation: 'Homologación BM',
  planner_settings: 'Parámetros',
  role: 'Rol',
  mep_settings: 'Parámetros de mise en place',
  mep_day_team: 'Personas de mise en place',
  racionado_settings: 'Parámetros de racionado',
  chill_slot: 'Hueco de abatidor',
  pesaje_settings: 'Parámetros de pesaje',
  week_planning_settings: 'Parámetros de planificación semanal',
}

const ACTION: Record<ConfigChangeAction, { label: string; tone: TagTone }> = {
  create: { label: 'Alta', tone: 'ok' },
  update: { label: 'Cambio', tone: 'info' },
  delete: { label: 'Baja', tone: 'danger' },
  move: { label: 'Orden', tone: 'neutral' },
}

/** The core shape's keys, in Spanish. A key not listed shows as it is. */
const FIELD_LABEL: Record<string, string> = {
  name: 'Nombre',
  description: 'Descripción',
  sections: 'Secciones',
  planningGroup: 'Grupo',
  workstationCode: 'Máquina',
  maxCapacityRecipes: 'Capacidad recetas',
  maxCapacityBroths: 'Capacidad caldos',
  deadTimeMinutes: 'Tiempo muerto (min)',
  position: 'Orden',
  recipeCode: 'Receta',
  machineFamily: 'Familia',
  minWeight: 'Peso mínimo',
  maxCapacity: 'Capacidad máxima',
  code: 'Código',
  kind: 'Tipo',
  wetacaCode: 'Plato Wetaca',
  bmCode: 'Plato BM',
  wetacaSemiCode: 'Semi Wetaca',
  bmSemiCode: 'Semi BM',
  preproductionGroupIds: 'Grupos de preproducción',
  shortageGroupIds: 'Grupos de semi que admiten falta',
  consolidatedRiceCodes: 'Arroces consolidados',
  cpPotCapacityGrams: 'Olla CP (g)',
  slackChannelIds: 'Canales de Slack',
}

/** Keys that identify the row rather than describe it: never a change worth reading. */
const IDENTITY_KEYS = new Set(['id'])

const VALUE_LABEL: Record<string, string> = { nocturnal: 'Nocturna', weekend: 'Fin de semana' }

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '—'
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? '(vacío)' : value.map(formatValue).join(', ')
  }
  if (typeof value === 'string') {
    return VALUE_LABEL[value] ?? value
  }
  if (typeof value === 'boolean') {
    return value ? 'sí' : 'no'
  }
  if (typeof value === 'number') {
    return String(value)
  }
  return JSON.stringify(value)
}

/** A value of one key: a role's sections by their labels, everything else as `formatValue` says. */
const formatField = (key: string, value: unknown): string =>
  key === 'sections' && Array.isArray(value)
    ? formatValue(value.map((section) => (typeof section === 'string' ? sectionLabel(section) : section)))
    : formatValue(value)

type DiffLine = { key: string; before: unknown; after: unknown }

/**
 * What changed, key by key: for an update or a move, only the keys whose value
 * differs; for a create, what the row was created with; for a delete, what it
 * was. Compared by content, so an array rewritten with the same entries is not
 * a change.
 */
export const diffLines = (before: ChangeState, after: ChangeState): Array<DiffLine> => {
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])]
  return keys
    .filter((key) => !IDENTITY_KEYS.has(key))
    .map((key) => ({ key, before: before?.[key] ?? null, after: after?.[key] ?? null }))
    .filter((line) => JSON.stringify(line.before) !== JSON.stringify(line.after))
}

/** The row a change is about, by what a person recognises it by — the stored key (a uuid) last. */
const subjectOf = ({ before, after, entityKey }: ConfigChange): string => {
  const state = after ?? before ?? {}
  for (const key of ['name', 'workstationCode', 'recipeCode', 'code', 'wetacaSemiCode']) {
    const value = state[key]
    if (typeof value === 'string' && value !== '') {
      return value
    }
  }
  return ['planner_settings', 'mep_settings', 'racionado_settings', 'pesaje_settings', 'week_planning_settings'].includes(entityKey) ? '' : entityKey
}

const ChangeDiff = ({ change }: { change: ConfigChange }): ReactElement => {
  const lines = diffLines(change.before, change.after)
  if (lines.length === 0) {
    return <span className={styles['admin-page__muted']}>Sin cambios de valor</span>
  }
  return (
    <div className={styles['admin-page__diff']}>
      {lines.map((line) => (
        <span key={line.key}>
          <span className={styles['admin-page__diff-key']}>{FIELD_LABEL[line.key] ?? line.key}: </span>
          {change.action !== 'create' && (
            <span className={styles['admin-page__diff-before']}>{formatField(line.key, line.before)}</span>
          )}
          {change.action === 'update' || change.action === 'move' ? ' → ' : null}
          {change.action !== 'delete' && (
            <span className={styles['admin-page__diff-after']}>{formatField(line.key, line.after)}</span>
          )}
        </span>
      ))}
    </div>
  )
}

type ChangesTableProps = {
  changes: Array<ConfigChange>
  caption: string
}

/**
 * The change log of roles or of the planner's configuration (`app.config_changes`),
 * newest first as the API answers it: who, when, what and a compact
 * before → after of the keys that changed.
 */
export const ChangesTable = ({ changes, caption }: ChangesTableProps): ReactElement => {
  const columns: Array<TableColumn<ConfigChange>> = [
    {
      key: 'when',
      header: 'Cuándo',
      render: (change) => <span className={styles['admin-page__number']}>{formatDateTime(change.changedAt)}</span>,
    },
    { key: 'who', header: 'Quién', render: (change) => change.changedBy?.name ?? 'Sistema', hideOnMobile: true },
    {
      key: 'what',
      header: 'Qué',
      render: (change) => {
        const subject = subjectOf(change)
        return subject === '' ? ENTITY_LABEL[change.entity] : `${ENTITY_LABEL[change.entity]} · ${subject}`
      },
    },
    {
      key: 'action',
      header: 'Acción',
      render: (change) => <Tag tone={ACTION[change.action].tone} label={ACTION[change.action].label} />,
    },
    { key: 'diff', header: 'Cambio', render: (change) => <ChangeDiff change={change} /> },
  ]

  return <Table columns={columns} rows={changes} rowKey={(change) => change.id} caption={caption} />
}
